import { Avatar, Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useOtherConsultants } from 'dashboard409a/hooks/useOtherConsultants';
import { getInitials } from 'utilities';
import { OtherConsultantCellProps } from './cells.props';
import {
  consultantCellAvatarWrapperSx,
  consultantCellExtraCountBadgeSx,
  consultantCellUpdatingAvatarProgressSx,
} from './ConsultantCell.styles';
import { PersonSearchPopover } from './personSearchPopover';

const ConsultantCell = ({ valuationRow, onOtherConsultantsUpdate }: OtherConsultantCellProps) => {
  const {
    anchorEl,
    searchTerm,
    searching,
    consultants,
    selectedConsultants,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleToggleConsultant,
    setSearchTerm,
    isUpdating,
  } = useOtherConsultants({ valuationRow, onOtherConsultantsUpdate });

  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  const firstConsultant = selectedConsultants[0] ?? null;
  const extraCount = Math.max(0, selectedConsultants.length - 1);

  const displayContent = firstConsultant ? (
    <Box
      data-activatable="true"
      onClick={handleClick}
      aria-label={
        extraCount > 0
          ? `${firstConsultant.name}, ${extraCount} more consultant${extraCount === 1 ? '' : 's'}`
          : firstConsultant.name
      }
      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
      {/* First consultant only in the circle; +N badge = additional people (popover has the full list). */}
      <Box sx={consultantCellAvatarWrapperSx}>
        <Avatar
          alt={firstConsultant.name}
          src={firstConsultant.imageSrc && firstConsultant.imageSrc !== '-' ? firstConsultant.imageSrc : undefined}
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: firstConsultant.name })}
        </Avatar>
        {extraCount > 0 && (
          <Box aria-hidden sx={consultantCellExtraCountBadgeSx}>
            +
            {extraCount}
          </Box>
        )}
        {isUpdating && <CircularProgress size={28} sx={consultantCellUpdatingAvatarProgressSx} />}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
        {firstConsultant.name}
      </Typography>
    </Box>
  ) : (
    <Box
      data-activatable="true"
      onClick={handleClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
      {isUpdating ? (
        <CircularProgress size={16} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  );

  const results = consultants.map(consultant => ({
    ...consultant,
    targetRecordId: (consultant as unknown as { targetRecordId?: string }).targetRecordId || '',
  }));

  return (
    <>
      {displayContent}
      <PersonSearchPopover
        open={open}
        anchorEl={anchorEl}
        popoverId={popoverId}
        onClose={handleClose}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        searching={searching}
        results={results}
        currentSelections={selectedConsultants}
        onToggleSelection={handleToggleConsultant}
        placeholder="Search for other consultants..."
        minWidth={320}
      />
    </>
  );
};

export default ConsultantCell;
